import { wrapToLoadingFn } from '@helpers';

import { generateReplacementStringFromObj } from '@modules/settings/replacements';

import PbDashboardToQueueSender from '../PbDashboardToQueueSender.vue';
import PbDashboardPeriodBadge from '../PbDashboardPeriodBadge.vue';

import { DashboardApiService } from '../../_services/dashboard.api.service';

import { formatWidthHeight } from '../../_helpers/dimensions';

const headers = Object.freeze([
  { text: 'Outer ID', value: 'id' },
  { text: 'Quantity', value: 'quantity' },
  { text: 'Estimated Time', value: 'estimatedTime' },
  { text: 'Expires', value: 'expires' },
  { text: 'Filters', value: 'filters' },
  { text: 'Shipping Period', value: 'shippingPeriod' },
  { text: 'VIP', value: 'vip', width: '1%' },
  { text: '', value: 'actions', width: '5%' },
]);

export default {
  name: 'PbDashboardJobsViewer',

  props: {
    printMachines: {
      type: Array,
      default: () => [],
    },
  },

  components: {
    PbDashboardToQueueSender,
    PbDashboardPeriodBadge,
  },

  data() {
    return {
      params: {},
      jobsData: {},
      selectedJobs: [],

      dialog: false,
      isSplit: false,

      toQueueSenderMenu: false,
      toQueueSenderDialog: false,
      toQueueSenderData: {
        substrate: '',
        machine: '',
        filters: {},
        item: {},
        minWidth: 0,
        minHeight: 0,
      },
    };
  },

  computed: {
    headers() {
      return (this.isSplit || this.params.isGroup)
        ? [{ name: '', value: 'checkboxes', width: '1%' }, ...headers]
        : headers;
    },

    jobs() {
      return this.jobsData.jobs || [];
    },

    splitQuantity() {
      const { selectedJobs, jobs } = this;

      return jobs.reduce((acc, { id, quantity }) => (
        selectedJobs.includes(id)
          ? acc + Number(quantity)
          : acc
      ), 0);
    },
  },

  methods: {
    $_openDialog(data) {
      this.$set(this, 'params', data);
      this.getJobs();
    },

    async getJobs({ isRefresh = false } = {}) {
      const action = this.params.isGroup
        ? DashboardApiService.getGroupJobs
        : DashboardApiService.getQueueJobs;

      let params;

      if (this.params.isGroup) {
        params = {
          machineCategory: this.params.machineCategory,
          shippingPeriods: this.params.shippingPeriods,
          groupFilter: this.params.groupFilter,
          operation: this.params.operation,
          substrate: this.params.substrate,
          height: this.params.height,
          width: this.params.width,
        };
      } else {
        params = { queueItemId: this.params.id };
      }

      return this.wrapToLoadingFn({
        req: action.bind({}, { suffix: this.params.suffix, ...params }),
        fullscreenLoadingSpinner: !isRefresh,
        onSuccess: (data) => {
          this.$set(this, 'jobsData', data);

          if (!isRefresh) {
            this.dialog = true;
          }
        },
      });
    },

    markJobAsVip(id, nextVipState) {
      this.$VBlackerTheme.openLoadingSpinner();

      this.wrapToLoadingFn({
        req: DashboardApiService.markJobAsVip.bind({}, {
          suffix: this.params.suffix,
          state: nextVipState,
          id,
        }),
        onSuccess: async () => {
          try {
            await this.getJobs({ isRefresh: true });
          } finally {
            this.$VBlackerTheme.closeLoadingSpinner();
          }
        },
        onReject: () => this.$VBlackerTheme.closeLoadingSpinner(),
      });
    },

    splitJobs() {
      if (!this.isSplit) {
        this.isSplit = true;
        return;
      }

      this.$VBlackerTheme.alert.warning({
        text: 'Split jobs in queue?',
      }, () => this.wrapToLoadingFn({
        req: DashboardApiService.splitQueue.bind({}, {
          splittedJobs: this.selectedJobs,
          machine: this.params.machine,
          queueItemId: this.params.id,
          suffix: this.params.suffix,
          jobs: this.jobs.reduce((acc, { id }) => {
            if (!this.selectedJobs.includes(id)) {
              acc.push(id);
            }

            return acc;
          }, []),
        }),
        fullscreenLoadingSpinner: true,
        onSuccess: () => {
          this.$VBlackerTheme.notification.success('Successfully splitted');
          // After successful splitting should refresh dashboard and close jobs viewer
          this.$emit('refresh');
          this.dialog = false;
        },
      }));
    },

    showSendToQueuesDialog(machine) {
      Object.assign(this.toQueueSenderData, {
        item: {
          operation: this.params.operation,
          substrate: this.params.substrate,
          fixedJobs: this.selectedJobs,
          height: this.params.height,
          width: this.params.width,
        },
        filters: {
          shippingPeriods: this.params.shippingPeriods,
          groupFilter: this.params.groupFilter,
        },
        substrate: generateReplacementStringFromObj({
          name: this.params.substrate,
          height: this.params.height,
          width: this.params.width,
        }),
        minWidth: this.params.printingAreaWidth ?? 0,
        minHeight: this.params.printingAreaHeight ?? 0,
        machine,
      });

      this.toQueueSenderDialog = true;
      this.toQueueSenderMenu = false;
    },

    onSendToQueueSuccess() {
      this.$VBlackerTheme.notification.success('Successfully sent');
      this.$emit('refresh');
      this.dialog = false;
    },

    sendToQueueSelectedJobs(machine) {
      this.wrapToLoadingFn({
        req: DashboardApiService.sendGroupItemToMachine.bind({}, {
          shippingPeriods: this.params.shippingPeriods,
          groupFilter: this.params.groupFilter,
          operation: this.params.operation,
          substrate: this.params.substrate,
          fixedJobs: this.selectedJobs,
          height: this.params.height,
          suffix: this.params.suffix,
          width: this.params.width,

          substrateReplacement: null,

          machine,
        }),
        fullscreenLoadingSpinner: true,
        onSuccess: () => {
          this.$VBlackerTheme.notification.success('Successfully sent');
          // After successful splitting should refresh dashboard and close jobs viewer
          this.$emit('refresh');
          this.dialog = false;
        },
      });
    },

    refreshSelectedJobs(id) {
      if (this.selectedJobs.includes(id)) {
        this.selectedJobs.splice(this.selectedJobs.indexOf(id), 1);
      } else {
        this.selectedJobs.push(id);
      }
    },

    resetViewerParams() {
      setTimeout(() => {
        this.$set(this, 'params', {});
        this.$set(this, 'jobsData', {});

        this.isSplit = false;
        this.selectedJobs = [];
      }, 100);
    },

    resetSplit() {
      this.selectedJobs = [];
      this.isSplit = false;
    },

    generateReplacementStringFromObj,
    formatWidthHeight,
    wrapToLoadingFn,
  },
};
